import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../lib/axiosInstance';
import {
    MdSearch,
    MdFilterList,
    MdChevronLeft,
    MdChevronRight,
    MdFileDownload,
    MdShoppingCart,
    MdVisibility,
    MdEvent,
    MdMoreVert,
    MdClose
} from 'react-icons/md';

// Modern Pagination Component
const Pagination = ({ currentPage, totalPages, onPageChange, hasNext, hasPrev }) => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
    }

    return (
        <div className="flex items-center justify-between mt-8 bg-[var(--surface)] p-4 rounded-3xl border border-[var(--border)] shadow-sm">
            <div className="text-xs font-bold text-[var(--muted)] uppercase tracking-widest px-4">
                Page {currentPage} of {totalPages}
            </div>
            <div className="flex items-center gap-2">
                <button
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={!hasPrev}
                    className="p-2 border border-[var(--border)] rounded-xl bg-[var(--surface)] text-[var(--text)] disabled:opacity-30 disabled:cursor-not-allowed hover:border-blue-500 transition-colors"
                >
                    <MdChevronLeft size={20} />
                </button>

                <div className="flex items-center gap-1">
                    {pages.map(page => (
                        <button
                            key={page}
                            onClick={() => onPageChange(page)}
                            className={`w-10 h-10 flex items-center justify-center rounded-xl font-bold text-sm transition-all ${page === currentPage
                                    ? 'bg-blue-600 text-white shadow-lg shadow-blue-200'
                                    : 'text-[var(--text)] hover:bg-[var(--bg)]'
                                }`}
                        >
                            {page}
                        </button>
                    ))}
                </div>

                <button
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={!hasNext}
                    className="p-2 border border-[var(--border)] rounded-xl bg-[var(--surface)] text-[var(--text)] disabled:opacity-30 disabled:cursor-not-allowed hover:border-blue-500 transition-colors"
                >
                    <MdChevronRight size={20} />
                </button>
            </div>
        </div>
    );
};

function OrderList() {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filters, setFilters] = useState({
        search: '',
        page: 1,
        limit: 10,
        itemStatus: '',
        startDate: '',
        endDate: ''
    });
    const [searchInput, setSearchInput] = useState('');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1,
        totalOrders: 0,
        hasNext: false,
        hasPrev: false
    });

    const fetchOrders = useCallback(async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams();
            Object.keys(filters).forEach(key => {
                if (filters[key]) params.append(key, filters[key]);
            });

            const { data } = await axiosInstance.get(`/brand/orders?${params}`);
            if (data.success) {
                setOrders(data.data);
                setPagination(data.pagination);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to fetch orders');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        fetchOrders();
    }, [fetchOrders]);

    const handleDownload = async () => {
        try {
            setError('');
            const params = new URLSearchParams();
            if (filters.search) params.append('search', filters.search);
            if (filters.itemStatus) params.append('itemStatus', filters.itemStatus);
            if (filters.startDate) params.append('startDate', filters.startDate);
            if (filters.endDate) params.append('endDate', filters.endDate);

            const response = await axiosInstance.get(`/brand/orders/export?${params.toString()}`, {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `orders_export_${new Date().getTime()}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            setError('Failed to download orders');
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            page: 1
        }));
    };

    const formatPrice = (price) => {
        return `₹${price?.toLocaleString('en-IN') || '0'}`;
    };

    const formatDate = (dateString) => {
        const d = new Date(dateString);
        return {
            date: d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }),
            time: d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        };
    };

    return (
        <div className="max-w-7xl mx-auto py-8 px-4">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10">
                <div>
                    <h1 className="text-4xl font-black text-[var(--text)] tracking-tighter flex items-center gap-3">
                        <MdShoppingCart className="text-blue-600" /> Order Archive
                    </h1>
                    <p className="text-[var(--muted)] font-medium mt-1">
                        Review and manage your brand's transactional history.
                    </p>
                </div>

                <div className="flex items-center gap-3">
                    <button
                        onClick={() => setIsFilterOpen(!isFilterOpen)}
                        className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-bold text-sm transition-all border ${isFilterOpen
                                ? 'bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-200'
                                : 'bg-[var(--surface)] text-[var(--text)] border-[var(--border)] hover:border-blue-500'
                            }`}
                    >
                        <MdFilterList size={20} />
                        {isFilterOpen ? 'Close Filters' : 'Filter Archive'}
                    </button>

                    <button
                        onClick={handleDownload}
                        className="flex items-center gap-2 px-6 py-3 bg-[var(--text)] text-[var(--bg)] rounded-2xl font-bold text-sm hover:opacity-90 transition-all shadow-lg"
                    >
                        <MdFileDownload size={20} />
                        Export Data
                    </button>
                </div>
            </div>

            {/* Filter Panel */}
            {isFilterOpen && (
                <div className="bg-[var(--surface)] p-8 rounded-[40px] border border-[var(--border)] mb-10 shadow-2xl shadow-blue-500/5 dark:shadow-blue-400/5 animate-in fade-in slide-in-from-top-4 duration-500">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                        {/* Search Input */}
                        <div className="md:col-span-2">
                            <label className="text-[10px] font-black tracking-widest text-[var(--muted)] uppercase mb-3 block">Search Context</label>
                            <div className="relative group">
                                <MdSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--muted)] group-focus-within:text-blue-600 transition-colors" size={20} />
                                <input
                                    type="text"
                                    placeholder="Enter Order ID or Item Names..."
                                    value={searchInput}
                                    onChange={(e) => setSearchInput(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleFilterChange('search', searchInput)}
                                    className="w-full pl-12 pr-4 py-4 bg-[var(--bg)] border-2 border-transparent rounded-2xl text-sm font-medium focus:bg-[var(--surface)] focus:border-blue-500 transition-all outline-none"
                                />
                            </div>
                        </div>

                        {/* Status Filter */}
                        <div>
                            <label className="text-[10px] font-black tracking-widest text-[var(--muted)] uppercase mb-3 block">Order Status</label>
                            <select
                                value={filters.itemStatus}
                                onChange={(e) => handleFilterChange('itemStatus', e.target.value)}
                                className="w-full px-4 py-4 bg-[var(--bg)] border-2 border-transparent rounded-2xl text-sm font-bold focus:bg-[var(--surface)] focus:border-blue-500 transition-all outline-none appearance-none cursor-pointer"
                            >
                                <option value="">All Statuses</option>
                                <option value="pending">Pending Preparation</option>
                                <option value="shipped">In Transit / Shipped</option>
                                <option value="delivered">Completed / Delivered</option>
                            </select>
                        </div>

                        {/* Date Filters Group */}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-[10px] font-black tracking-widest text-[var(--muted)] uppercase mb-3 block">From</label>
                                <input
                                    type="date"
                                    value={filters.startDate}
                                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                                    className="w-full px-4 py-4 bg-[var(--bg)] border-2 border-transparent rounded-2xl text-xs font-bold focus:bg-[var(--surface)] focus:border-blue-500 transition-all outline-none"
                                />
                            </div>
                            <div>
                                <label className="text-[10px] font-black tracking-widest text-[var(--muted)] uppercase mb-3 block">To</label>
                                <input
                                    type="date"
                                    value={filters.endDate}
                                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                                    className="w-full px-4 py-4 bg-[var(--bg)] border-2 border-transparent rounded-2xl text-xs font-bold focus:bg-[var(--surface)] focus:border-blue-500 transition-all outline-none"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-between mt-8 pt-8 border-t border-[var(--border)]">
                        <div className="text-xs text-[var(--muted)] font-medium italic">
                            {(filters.search || filters.startDate || filters.endDate || filters.itemStatus) ? 'Archive filtered by custom parameters' : 'Archive showing all records'}
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={() => {
                                    setSearchInput('');
                                    setFilters({ search: '', page: 1, limit: 10, itemStatus: '', startDate: '', endDate: '' });
                                }}
                                className="px-6 py-2 text-red-500 font-bold text-xs uppercase tracking-widest hover:underline"
                            >
                                Reset All
                            </button>
                            <button
                                onClick={() => handleFilterChange('search', searchInput)}
                                className="px-8 py-3 bg-blue-600 text-white rounded-2xl font-bold text-sm shadow-lg shadow-blue-200"
                            >
                                Apply Parameters
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {error && (
                <div className="bg-red-50 border-2 border-red-100 text-red-600 px-6 py-4 rounded-3xl mb-8 flex items-center justify-between">
                    <span className="font-bold text-sm">Error: {error}</span>
                    <button onClick={() => setError('')}><MdClose size={20} /></button>
                </div>
            )}

            {/* Table Section */}
            <div className="bg-[var(--surface)] border border-[var(--border)] rounded-[48px] overflow-hidden shadow-2xl shadow-slate-100/30 dark:shadow-blue-900/10 transition-all">
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead>
                            <tr className="bg-[var(--bg)]/50 border-bottom border-[var(--border)]">
                                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-[var(--muted)]">Archive Reference</th>
                                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-[var(--muted)]">Merchant Cargo</th>
                                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-[var(--muted)] text-center">Settlement Value</th>
                                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-[var(--muted)] text-center">Inventory Count</th>
                                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-[var(--muted)] text-right">Fulfillment Date</th>
                                <th className="px-8 py-6 text-[10px] font-black uppercase tracking-widest text-[var(--muted)] text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[var(--border)]">
                            {loading ? (
                                Array(5).fill(0).map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        <td colSpan={6} className="px-8 py-10">
                                            <div className="h-4 bg-[var(--bg)] rounded-full w-full"></div>
                                        </td>
                                    </tr>
                                ))
                            ) : orders.length > 0 ? (
                                orders.map((order) => {
                                    const { date, time } = formatDate(order.createdAt);
                                    return (
                                        <tr key={order.orderID} className="group hover:bg-blue-50/10 dark:hover:bg-blue-600/5 transition-all duration-300">
                                            <td className="px-8 py-6">
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-black text-blue-500 tracking-tighter">#{order.orderID}</span>
                                                    <span className="text-[10px] font-bold text-[var(--muted)] uppercase mt-0.5 opacity-60">ID: {order.uid.slice(0, 8)}...</span>
                                                </div>
                                            </td>
                                            <td className="px-8 py-6">
                                                <div className="max-w-[240px]">
                                                    <div className="text-sm font-bold text-[var(--text)] truncate group-hover:text-blue-500 transition-colors" title={order.itemNames}>
                                                        {order.itemNames}
                                                    </div>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <span className="flex w-1.5 h-1.5 rounded-full bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.6)] animate-pulse"></span>
                                                        <span className="text-[10px] font-black text-[var(--muted)] uppercase tracking-tighter leading-none">Archival Bundle</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-8 py-6 text-center">
                                                <span className="text-lg font-black text-[var(--text)] tracking-tighter tabular-nums bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent">{formatPrice(order.total)}</span>
                                            </td>
                                            <td className="px-8 py-6 text-center">
                                                <div className="inline-flex items-center justify-center w-8 h-8 rounded-xl bg-[var(--bg)] text-[10px] font-black border border-[var(--border)] group-hover:border-blue-500/30 transition-all">
                                                    {order.itemCount}
                                                </div>
                                            </td>
                                            <td className="px-8 py-6 text-right">
                                                <div className="flex flex-col items-end">
                                                    <span className="text-xs font-black text-[var(--text)]">{date}</span>
                                                    <span className="text-[10px] font-bold text-[var(--muted)] uppercase tracking-tighter">{time}</span>
                                                </div>
                                            </td>
                                            <td className="px-8 py-6 text-right">
                                                <Link
                                                    to={`/orders/${order.orderID}`}
                                                    className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--surface)] border border-[var(--border)] rounded-xl text-xs font-black text-blue-500 uppercase tracking-widest hover:bg-blue-600 hover:text-white hover:border-blue-600 hover:shadow-lg hover:shadow-blue-500/30 transition-all duration-300"
                                                >
                                                    <MdVisibility size={14} />
                                                    Details
                                                </Link>
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={6} className="py-32">
                                        <div className="flex flex-col items-center justify-center text-center opacity-40">
                                            <MdShoppingCart size={64} className="mb-4" />
                                            <h3 className="text-2xl font-black tracking-tighter">No archival records match.</h3>
                                            <p className="text-sm font-medium">Try broadening your search parameters.</p>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination Section */}
            {!loading && pagination.totalPages > 1 && (
                <Pagination
                    currentPage={pagination.currentPage}
                    totalPages={pagination.totalPages}
                    onPageChange={(page) => handleFilterChange('page', page)}
                    hasNext={pagination.hasNext}
                    hasPrev={pagination.hasPrev}
                />
            )}
        </div>
    );
}

export default OrderList;

